import React, { Component, PropTypes } from 'react'
import styled from 'styled-components'
import R from 'ramda'
import Grid, { Half } from 'grid-styled'
import { translate } from 'react-i18next'
import CardContainer from '../../components/CardContainer'
import { Metrics, Fonts } from '../../Themes'
import Input from '../../components/Input'
import Button from '../../components/Button'
import { validateInput, validateForm } from '../../Utils/Validations'
import { generateNCNDAWebsiteAgreement } from '../../sources/ContractsSource'
import Loader from '../../components/Loader'

const CustomGrid = styled(Grid)`
  padding-left: ${Metrics.margins.medium};
  padding-right: ${Metrics.margins.medium};
  button {
    margin-top: ${Metrics.margins.base};
    float: right
  }

  @media screen and (max-width: ${Metrics.breakpoints.mobile}) {
    padding: 0;
  }
`

const CustomHalf = styled(Half)`
  margin-top: ${props => (props.noPadTop ? 0 : Metrics.margins.base)};
  padding-left: ${Metrics.margins.medium};
  padding-right: ${Metrics.margins.medium};

  @media screen and (max-width: ${Metrics.breakpoints.mobile}) {
    padding: 0;
  }
`

const CustomCardContainer = styled(CardContainer)`
  height: 100%;
`

const List = styled.ul`
  padding-left: 0;
  list-style: none;
  ${Fonts.style.Text};

  li {
    margin: ${Metrics.margins.base} 0;
    padding-bottom: ${Metrics.margins.small};
    border-bottom: ${Metrics.borders.default};

    li {
      padding-bottom: 0;
      margin: ${Metrics.margins.small} 0;
      border-bottom: none;
    }
  }
`

const OrderedList = styled.ol`
  padding-left: 0;
  ${Fonts.style.Text};

  counter-reset: item;
  li:before {
    content: counters(item, ".") ".";
    counter-increment: item;
  }
  ol {
    padding-left: ${Metrics.margins.medium};
  }

  li {
    display: block;
    margin: ${Metrics.margins.small} 0;
    border-bottom: none;
  }
`

const Terms = styled.div`
  ${Fonts.style.Text};
  margin-top: ${Metrics.margins.base};
`

const Title = styled.div`
  margin-bottom: ${Metrics.margins.small};
  ${Fonts.style.Label};
`

class NDFA extends Component {
  constructor (props) {
    super(props)
    this.state = {
      name: '',
      company: '',
      address: '',
      phoneNumber: '',
      email: '',
      function: '',
      generating: false,
      errors: {
        name: '',
        company: '',
        address: '',
        phoneNumber: '',
        email: '',
        function: ''
      }
    }
    this.handleChange = this.handleChange.bind(this)
    this.downloadFile = this.downloadFile.bind(this)
    this.handleValidate = this.handleValidate.bind(this)
  }

  handleChange (event, key) {
    const state = R.clone(this.state)
    state[key] = event.target.value
    this.setState(state)
  }

  downloadFile () {
    const { errors } = this.state
    let validation = validateForm(this.state, errors)

    if (validation.valid) {
      this.setState({ generating: true }, () => {
        generateNCNDAWebsiteAgreement({
          name: this.state.name,
          company: this.state.company,
          address: this.state.address,
          phoneNumber: this.state.phoneNumber,
          email: this.state.email,
          function: this.state.function
        })
          .then(response => {
            this.setState({ generating: false })
            document.location = response.data.url
          })
          .catch(console.error)
      })
    } else {
      this.setState({ errors: validation.errors })
    }
  }

  handleValidate (name, value, errors) {
    let newErrors = validateInput(name, value, errors)
    this.setState({ errors: newErrors })
  }

  render () {
    const { t } = this.props
    const { errors } = this.state
    let keys = [
      { key: 'name', label: t('Name'), placeholder: t('Name') },
      { key: 'company', label: t('Company'), placeholder: t('Company Name') },
      {
        key: 'address',
        label: t('Address'),
        placeholder: t('Company Address')
      },
      { key: 'phoneNumber', label: t('Phone'), placeholder: t('Phone Number') },
      { key: 'email', label: t('E-mail'), placeholder: t('E-mail') },
      { key: 'function', label: t('Function'), placeholder: t('Function') }
    ]

    return (
      <CustomCardContainer
        title={t('Non-Circumvention / Non-Disclosure and Fee Agreement')}>
        <Loader visible={this.state.generating} />
        <CustomGrid>
          <Terms>
            <p>
              Whereby{' '}
              <b>{this.state.company || this.state.name || t('NAME')}</b>{' '}
              requested access to information on assets in the portfolio of HvB.
            </p>
            <p>
              Whereby the Parties desire to disclose, on a confidential basis,
              certain information, process, clients, business contacts and
              documents considered confidential and/or proprietary by the
              Parties concerning their respective businesses.
            </p>
            <p>
              The Parties wish to maintain the confidentiality and/or the
              proprietary nature of the confidential information disclosed.
            </p>
            By this Agreement, the Parties wish to set the basis for a lasting
            business relationship.
            <List>
              <li>
                <Title>Definitions</Title>
                <OrderedList>
                  <li>
                    “Business Associates” means, business affiliates, investors,
                    financers, clients, employees, vendors, asset owners and any
                    other third-party relations.
                  </li>
                  <li>
                    “Client” means third parties directly interested in
                    acquiring or selling real estate or other assets.
                  </li>
                  <li>
                    “Commission” means, any monetary compensation paid by a
                    Client, a Business Associate or any other person and/or
                    entity in consideration of introductions made and/or
                    transactions successfully closed.
                  </li>
                  <li>
                    “Confidential Information” means any and all information
                    disclosed orally or in writing or made available by any
                    Party ("Disclosing Party") to another Party ("Recipient") so
                    long as not explicitly designated as not confidential by the
                    Disclosing Party. In addition, the existence and terms of
                    this Agreement shall also be treated as Confidential
                    Information.
                  </li>
                  <li>
                    “Transaction” means, the sale of one (1) asset either
                    directly listed in HvB’s portfolio or sold through or by a
                    Business Associate of HvB.
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Confidentiality and Non-Disclosure</Title>
                <OrderedList>
                  <li>
                    The Parties agree to maintain the confidentiality of the
                    Confidential Information and to prevent its unauthorized
                    dissemination or use, subject to the exceptions enumerated
                    in Section 2.5. of this Agreement.
                  </li>
                  <li>
                    The Parties agree not to use the Confidential Information
                    for purposes other than necessary to consider and enable the
                    business relationship or pursue transaction between or
                    involving the Parties or one of the Parties.
                  </li>
                  <li>
                    The Recipient shall protect the Confidential Information by
                    using a high degree of care, to prevent the unauthorized
                    use, dissemination or publication of the Confidential
                    Information.
                  </li>
                  <li>
                    The Recipient shall limit its internal disclosure of the
                    Confidential Information to only those employees,
                    accountants, attorneys and affiliates who have a provable
                    need to access the information for the limited purpose of
                    the proposed business relationship or transaction between
                    the Parties.
                  </li>
                  <li>
                    This Agreement shall impose no obligations with respect to
                    Confidential Information which:
                    <OrderedList>
                      <li>
                        is disclosed with the prior written consent of the
                        Disclosing Party;
                      </li>
                      <li>
                        is now, or hereafter becomes, through no act or failure
                        to act on the part of the Recipient, generally known or
                        available to the public;
                      </li>
                      <li>
                        was already known by Recipient before receiving such
                        information from the Disclosing Party, as evidenced by
                        written documents;
                      </li>
                      <li>
                        is information which the Recipient can document was
                        independently developed by the Recipient without
                        reference to or use of any Confidential Information
                        received from the Disclosing Party;
                      </li>
                      <li>
                        is required to be disclosed pursuant governmental
                        agencies or law. In such cases, the Recipient seeking to
                        disclose Confidential Information will provide notice to
                        the Disclosing Party prior to such disclosure to afford
                        the Disclosing Party a reasonable opportunity to file
                        objections to the disclosure with the appropriate agency
                        or entity. In case Confidential Information must legally
                        be disclosed, the Disclosing Party is responsible to
                        disclose not more than the absolute minimum necessary.
                      </li>
                    </OrderedList>
                  </li>
                  <li>
                    The Recipient acknowledges and agrees that all the
                    Confidential Information of the Disclosing Party is and
                    shall remain the sole and exclusive property of the
                    Disclosing Party, free of any and all claims of the
                    Recipient.
                  </li>
                  <li>
                    Each Party shall be responsible for compliance with all
                    applicable federal, state and local (in foreign
                    jurisdictions) laws and regulations, including all
                    applicable rules of self-regulatory organizations. To the
                    extent that either Party fails to comply with such
                    regulations, it shall indemnify the other Party or parties
                    from any damages resulting from the violation.
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Non-Circumvention and Non-Solicitation</Title>
                <OrderedList>
                  <li>
                    The Parties agree not to directly or indirectly circumvent
                    each other and not to work/ deal/ transact with Business
                    Associates introduced by each Party at any time, without the
                    knowledge and explicit permission of the introducing Party.
                    This clause is valid indefinitely, and is not affected by a
                    possible cancellation of this Agreement.
                  </li>
                  <li>
                    In case of unauthorized circumvention or solicitation, in
                    addition to the Commission usually due to the affected
                    Party, the violating Party is liable to pay to the affected
                    Party a penalty of 5% of the total Transaction value.
                  </li>
                  <li>
                    The payment of the penalty in no way transfers the ownership
                    of the Client or Business Associate to the violating Party.
                    Any further Transaction with the concerned Client or
                    Business Associate, without express approval of the affected
                    Party, shall trigger this penalty anew.
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Commission</Title>
                <OrderedList>
                  <li>
                    <b>
                      {this.state.company || this.state.name || t('NAME')}
                    </b>{' '}
                    agrees to pay, in consideration for the introduction made by
                    HvB, a per deal Commission of 2.25% of the total Transaction
                    value directly to HvB in case of a successful Transaction.
                  </li>
                  <li>
                    The Commission is to be paid to the account defined by HvB
                    latest 7 (seven) days after performance of the Investment.
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Validity and Termination</Title>
                <OrderedList>
                  <li>
                    This Agreement shall be valid once signed by both Parties by
                    the date stated on the agreement.
                  </li>
                  <li>
                    This Agreement may be terminated by either Party for any
                    reason and at any time, by written notice given to the other
                    Party. However, any such termination shall have no effect on
                    the rights and obligations of the Parties under this
                    Agreement in respect to Transactions executed, or in the
                    process of being executed prior to such termination and
                    especially with regards to Confidential Information, client
                    and contact protection.
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Amendments</Title>
                <OrderedList>
                  <li>
                    Any amendment of this Agreement shall only be valid if made
                    in written form and if signed by both Parties.
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Governing law and jurisdiction</Title>
                <OrderedList>
                  <li>
                    This Agreement shall in all respects be governed by and
                    construed in accordance with Swiss law, without giving
                    effect to its conflict of law principles. All disputes
                    arising from or in connection with this Agreement, including
                    disputes regarding its conclusion, validity, binding effect,
                    amendment, breach, termination or rescission, shall be
                    subject to the exclusive jurisdiction of the courts of the
                    Canton of Zurich.
                  </li>
                </OrderedList>
              </li>
            </List>
          </Terms>
        </CustomGrid>

        {keys.map((el, index) => {
          return (
            <CustomHalf
              key={el.key}
              noPadLeft={index % 2 === 0}
              noPadRight={index % 2 === 1}>
              <Input
                placeholder={el.placeholder}
                name={el.key}
                required
                title={el.label}
                value={this.state[el.key]}
                handleChange={event => this.handleChange(event, el.key)}
                error={errors[el.key]}
                handleBlur={event =>
                  this.handleValidate(el.key, this.state[el.key], errors)}
              />
            </CustomHalf>
          )
        })}
        <CustomGrid>
          <Button label={t('Download')} onClick={this.downloadFile} />
          <Button label={t('Download')} onClick={this.downloadFile} />
        </CustomGrid>
      </CustomCardContainer>
    )
  }
}

NDFA.propTypes = {
  t: PropTypes.func,
  router: PropTypes.object,
  children: PropTypes.node
}

export default translate([ 'common', 'forms', 'admin' ], { wait: true })(NDFA)



// WEBPACK FOOTER //
// ./src/pages/Agreements/NDFA.js